import clsx from 'clsx';
import {
  type ChangeEvent,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { CheckmarkSmallIcon, MinusIcon } from '@dynatrace/strato-icons';

import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useAriaLabelingProps as _useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useSafeForwardProps as _useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { focusRingStatic } from '../../core/styles/focusRingStatic.sty.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { fieldTokens as fieldColorsCSS } from '../../styles/field.sty.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormControl } from '../forms-core/hooks/useFormControl.js';
import {
  getI18nValidationState,
  useFormFieldValidation,
} from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useContainerFocus } from '../hooks/useContainerFocus.js';
import type { FormControlRef } from '../shared-types.js';
import type { MessageTarget } from '../validation/message-routing.js';
import type { ValidationState } from '../validation/types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import {
  checkboxIconCSS,
  checkboxKnobCSS,
  checkboxLabelCSS,
} from './Checkbox.sty.js';

const COMPONENT_NAME = 'Checkbox';

// #region Typings
/**
 * @public
 */
export type CheckboxProps = FormControlProps<
  boolean | 'indeterminate',
  (checked: boolean, event?: ChangeEvent<HTMLInputElement>) => void | never,
  HTMLDivElement
> &
  StylingProps &
  DataTestId &
  WithChildren &
  MaskingProps &
  BehaviorTrackingProps & {
    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
    /**
     * Handler that is called when the element's focus status changes.
     * @deprecated Use onFocus/onBlur instead.
     */
    onFocusChange?: (isFocused: boolean) => void;
    /**
     * A string representing the value used for the checkbox.
     * When submitted in a form, it is only submitted if checked, with the set value.
     * If undefined, the string 'on' is submitted by default as per https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#attr-value.
     */
    formValue?: string;
  };
// #endregion

/**
 * Use the `Checkbox` to let users select one or more options from a list, or to confirm a selection that requires a further action such as clicking a submit button.
 * @public
 */
export const Checkbox = forwardRef<FormControlRef, CheckboxProps>(
  (props, forwardedRef) => {
    const {
      disabled: propDisabled,
      required: propRequired,
      readOnly: propReadOnly,
      id: propId,
      defaultValue,
      value,
      name,
      _form,
      _messageTarget,
      formValue,
      onValidityChange,
      onChange,
      children,
      // oxlint-disable-next-line typescript/no-deprecated -- APPDEV-18321
      controlState,
      className: consumerClassName,
      style: consumerStyle,
      'aria-disabled': ariaDisabledProp,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      onFocus,
      onBlur,
      ...remainingProps
    }: CheckboxProps = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const ariaLabelingProps = _useAriaLabelingProps(restProps);
    const forwardProps = _useSafeForwardProps(restProps, ariaLabelingProps);

    const {
      id,
      disabled,
      required,
      ariaDisabled,
      readOnly: _readOnly,
      inputProps,
      hintProps,
      hint,
      showError,
      messageState: messagesContextState,
      messageDispatch: messagesContextDispatch,
    } = useFormControl({
      propId,
      propDisabled,
      propRequired,
      propAriaDisabled: ariaDisabledProp,
      propReadOnly,
      controlState,
    });

    // Imperative Handle Construction
    const intl = useIntl();
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const controlRef = useRef<FormControlRef>(null);
    const combinedRef = useMergeRefs<FormControlRef | null>([
      forwardedRef,
      controlRef,
    ]);
    const formValidityChangeRef = useRef<
      ((state: ValidationState) => void) | undefined
    >(undefined);
    const _emitValidationState = useValidationStateEmitter(
      controlState
        ? undefined
        : (state) => formValidityChangeRef.current?.(state),
    );
    const emitValidationState = useCustomMessageEmitter(
      _emitValidationState,
      () => getI18nValidationState(intl, inputRef.current, 'manual'),
    );
    const {
      onChange: validationOnChange,
      onInvalid: validationOnInvalid,
      ariaProps,
    } = useFormFieldValidation(
      inputRef,
      { required, value: String(value) },
      ariaLabelingProps,
      undefined,
      _emitValidationState,
      controlState,
    );

    useImperativeHandle(combinedRef, (): FormControlRef => {
      return {
        element: rootRef.current,
        inputRef: inputRef.current,
        focus() {
          inputRef.current?.focus();
        },
        validate() {
          if (messagesContextState) {
            inputRef.current?.checkValidity();
            messagesContextDispatch?.({
              type: FormFieldMessagesAction.SETISTOUCHED,
              payload: { isTouched: true },
            });
            emitValidationState(
              getI18nValidationState(intl, inputRef.current, 'manual'),
            );
          }
        },
        // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
        validateAsync: async () => {
          inputRef.current?.checkValidity();
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          return emitValidationState(
            getI18nValidationState(intl, inputRef.current, 'manual'),
          );
        },
      };
    });

    const [state, setState] = useControllableState({
      defaultValue,
      value,
    });

    const formControl = useInternalFormRegistration({
      id,
      name,
      form: _form,
      messageTarget: _messageTarget,
      value: state === true,
      controlRef,
      onValidityChange,
    });
    formValidityChangeRef.current = formControl.onValidityChange;

    const disabledOrAriaDisabled = disabled || ariaDisabled || false;
    const readOnly = !!_readOnly;

    let focusRingColor: 'neutral' | 'critical' | 'primary' = 'neutral';
    if (showError) {
      focusRingColor = 'critical';
    } else if (!(disabled || readOnly) && state) {
      focusRingColor = 'primary';
    }

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
      if (readOnly) {
        return;
      }

      const newValue = e.target.indeterminate ? true : e.target.checked;
      setState(newValue);
      onChange?.(newValue, e);
      validationOnChange();
    };

    const { containerClassName, ...containerProps } =
      useContainerFocus<HTMLDivElement>({
        onFocus,
        onBlur,
        disabled,
        emitValidationState,
      });

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = state === 'indeterminate';
      }
    }, [state]);

    let fieldColorVariant: 'default' | 'accent' | 'emphasized' = 'default';
    if (state === true || state === 'indeterminate') {
      fieldColorVariant = 'accent';
    } else if (showError || disabledOrAriaDisabled) {
      fieldColorVariant = 'emphasized';
    }

    let fieldColorColor: 'neutral' | 'primary' | 'critical' = 'primary';
    if (showError) {
      fieldColorColor = 'critical';
    } else if (state === false) {
      fieldColorColor = 'neutral';
    }

    return (
      <Flex
        ref={rootRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        role="group"
        flexDirection="column"
        gap={4}
        className={clsx(containerClassName, consumerClassName)}
        style={consumerStyle}
        {...containerProps}
        {...mergeProps(forwardProps, behavioralTrackingProps)}
        data-dt-component={COMPONENT_NAME}
      >
        <label
          htmlFor={id}
          className={clsx(
            checkboxLabelCSS({
              required: typeof children === 'string' && required,
              disabled: disabledOrAriaDisabled,
              readOnly,
            }),
            textStyleCSS({ fontStyle: 'text', textStyle: 'base' }),
          )}
        >
          <div
            className={clsx(
              checkboxKnobCSS({
                disabled: disabledOrAriaDisabled,
                hasChildren: !!children,
                hasError: showError,
                checked: state === true || state === 'indeterminate',
                readOnly,
              }),
              fieldColorsCSS({
                variant: fieldColorVariant,
                disabled: disabledOrAriaDisabled,
                color: fieldColorColor,
              }),
              focusRingStatic({
                when: 'focus-within-visible',
                color: focusRingColor,
                disabled,
              }),
            )}
          >
            <VisuallyHidden>
              <input
                id={id}
                ref={inputRef}
                type="checkbox"
                aria-required={required}
                aria-disabled={ariaDisabled}
                readOnly={readOnly}
                name={name}
                form={_form}
                value={formValue}
                disabled={disabled}
                required={required}
                checked={state === true}
                onChange={handleOnChange}
                onInvalid={validationOnInvalid}
                {...(controlState ? inputProps : ariaProps)}
              />
            </VisuallyHidden>
            <div className={checkboxIconCSS({ readOnly, hasError: showError })}>
              <CheckboxIconSvg state={state} />
            </div>
          </div>
          {children}
        </label>
        {hint && (
          <BaseItem
            variant={showError ? 'error' : 'hint'}
            disabled={disabledOrAriaDisabled}
            {...hintProps}
          >
            {hint}
          </BaseItem>
        )}
      </Flex>
    );
  },
);
(Checkbox as typeof Checkbox & { displayName: string }).displayName =
  COMPONENT_NAME;

type CheckboxIconProps = {
  state: boolean | 'indeterminate';
};

/** Determines what svg to display based on the checked and indeterminate attributes. */
const CheckboxIconSvg = memo((props: CheckboxIconProps) => {
  switch (props.state) {
    case true:
      return <CheckmarkSmallIcon />;
    case 'indeterminate':
      return <MinusIcon />;
    case false:
    default:
      return null;
  }
});
